import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { IAirport } from '../../interfaces/IAirport';

interface AirportListExportEntry {
  type: string;
  name?: string;
  data?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class AirportService {

  private readonly _airportListUrl = 'assets/airport_list.json';
  private _airports$!: Observable<IAirport[]>;

  constructor(private _http: HttpClient) { }

  // Loads and caches the airport list from assets so the JSON is only fetched/parsed once.
  getAirports(): Observable<IAirport[]> {
    if (!this._airports$) {
      this._airports$ = this._http.get<AirportListExportEntry[]>(this._airportListUrl).pipe(
        map(exportData => this.mapToAirports(exportData)),
        shareReplay(1)
      );
    }
    return this._airports$;
  }

  // Returns airports matching the given term against airport code or name, best matches first.
  searchAirports(term: string, limit: number = 15): Observable<IAirport[]> {
    const normalizedTerm = (term || '').trim().toLowerCase();
    if (!normalizedTerm) {
      return of([]);
    }

    return this.getAirports().pipe(
      map(airports => this.filterAndRank(airports, normalizedTerm).slice(0, limit))
    );
  }

  // ...existing code...

  private filterAndRank(airports: IAirport[], term: string): IAirport[] {
    const exactMatches: IAirport[] = [];
    const startsWithMatches: IAirport[] = [];
    const containsMatches: IAirport[] = [];

    for (const airport of airports) {
      const matchType = this.getMatchType(airport, term);

      if (matchType === 'exact') {
        exactMatches.push(airport);
      } else if (matchType === 'startsWith') {
        startsWithMatches.push(airport);
      } else if (matchType === 'contains') {
        containsMatches.push(airport);
      }
    }

    return [...exactMatches, ...startsWithMatches, ...containsMatches];
  }

  private getMatchType(airport: IAirport, term: string): 'exact' | 'startsWith' | 'contains' | 'none' {
    const searchValues = this.getSearchableValues(airport);

    for (const value of searchValues) {
      const normalizedValue = value.toLowerCase();

      if (normalizedValue === term) {
        return 'exact';
      }

      if (normalizedValue.startsWith(term)) {
        return 'startsWith';
      }

      if (normalizedValue.includes(term)) {
        return 'contains';
      }
    }

    return 'none';
  }

  private getSearchableValues(airport: IAirport): string[] {
    return Object.entries(airport)
      .map(([, value]) => value?.toString() ?? '')
      .filter(value => value.length > 0);
  }

  private mapToAirports(exportData: AirportListExportEntry[]): IAirport[] {
    const table = exportData?.find(entry => entry.type === 'table' && entry.name === 'airport_list');
    const rows = table?.data ?? [];

    return rows
      .filter(row => !!row.airport_code)
      .map(row => ({
        id: row.airport_id,
        code: row.airport_code,
        name: row.airport_name,
        cityCode: row.airport_city_code,
        cityName: row.airport_city_name,
        countryName: row.airport_country_name,
        countryCode: row.airport_country_code
      }));
  }

}
